import { itemsApi } from "#/api/itemsApi";
import { showSuccessNotification } from "#/components/notifications";
import type { Item } from "#/types/items";
import { describeItem } from "#/utils/itemLabel";
import { logger } from "#/utils/logger";
import { Button, Loader, Modal, MultiSelect, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AddRelatedItemsModalProps {
  opened: boolean;
  onClose: () => void;
  /** Saved item the links are added to, or null while the item is being created. */
  itemId: number | null;
  /** Ids to hide from the results — used for picks staged during creation. */
  excludedIds: number[];
  /** Called after links are written on a saved item. */
  onLinked: () => void;
  /** Called with the picked items when there is no saved item to write to yet. */
  onStaged: (items: Item[]) => void;
}

/**
 * Search-and-pick dialog for linking items. Search runs on the backend against
 * external id and RFID. For a saved item the backend also filters out anything
 * already linked; while creating, the caller passes the staged picks instead.
 */
const AddRelatedItemsModal = ({
  opened,
  onClose,
  itemId,
  excludedIds,
  onLinked,
  onStaged,
}: AddRelatedItemsModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Labels of every item seen so far. Picked items must keep their label even
  // after the search moves on and they drop out of the current result set.
  const [seen, setSeen] = useState<Record<string, Item>>({});

  // Every dialog opens fresh — a stale search or selection would be confusing.
  useEffect(() => {
    if (opened) {
      setSearch("");
      setSelectedIds([]);
      setSeen({});
    }
  }, [opened]);

  const { data: candidates = [], isFetching } = useQuery({
    queryKey: ["available-related-items", itemId, debouncedSearch],
    queryFn: async () => {
      const params = { limit: 50, ...(debouncedSearch ? { search: debouncedSearch } : {}) };
      // No saved item yet: fall back to the plain item list.
      const response = itemId
        ? await itemsApi.getAvailableRelatedItems(itemId, params)
        : await itemsApi.getAll(params);
      return response.data.data;
    },
    enabled: opened,
    staleTime: 1000 * 30,
  });

  useEffect(() => {
    if (candidates.length === 0) return;
    setSeen((previous) => {
      const next = { ...previous };
      for (const item of candidates) next[String(item.id)] = item;
      return next;
    });
  }, [candidates]);

  const linkMutation = useMutation({
    mutationFn: async (ids: number[]) => itemsApi.linkRelatedItems(itemId!, ids),
    onSuccess: () => {
      showSuccessNotification({ message: t("items.relatedItems.linked") });
      onLinked();
      onClose();
    },
    onError: (error) => {
      logger.error("Failed to link related items:", error);
    },
  });

  const excluded = new Set(excludedIds.map(String));
  const visibleCandidates = candidates.filter((item) => !excluded.has(String(item.id)));
  const candidateIds = new Set(visibleCandidates.map((item) => String(item.id)));

  // Current results, plus any picked item the latest search no longer returns.
  const options = [
    ...visibleCandidates.map((item) => ({
      value: String(item.id),
      label: describeItem(item),
    })),
    ...selectedIds
      .filter((id) => !candidateIds.has(id))
      .map((id) => ({
        value: id,
        label: seen[id] ? describeItem(seen[id]) : `#${id}`,
      })),
  ];

  const handleSave = () => {
    const ids = selectedIds.map(Number);
    if (itemId) {
      linkMutation.mutate(ids);
      return;
    }
    // Nothing to write to yet — hand the picks back to the page.
    onStaged(ids.map((id) => seen[String(id)]).filter(Boolean));
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      title={t("items.relatedItems.addTitle")}
    >
      <div className="flex flex-col gap-4">
        <Text size="xs" c="dimmed">
          {t("items.relatedItems.addDescription")}
        </Text>

        <MultiSelect
          label={t("items.relatedItems.searchLabel")}
          placeholder={t("items.relatedItems.searchPlaceholder")}
          data={options}
          value={selectedIds}
          onChange={setSelectedIds}
          onSearchChange={setSearch}
          searchable
          hidePickedOptions
          clearable
          rightSection={isFetching ? <Loader size={14} /> : undefined}
          nothingFoundMessage={
            isFetching ? t("common.loading") : t("items.relatedItems.noMatches")
          }
        />

        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" color="black" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="filled"
            color="black"
            disabled={selectedIds.length === 0}
            loading={linkMutation.isPending}
            onClick={handleSave}
          >
            {t("common.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddRelatedItemsModal;
